import { DataGrid, DataGridProps, GridColDef } from '@mui/x-data-grid'
import { ActionRequest, Company, Device, Project, RuuviTag, User } from '../types'
import { useTranslation } from 'react-i18next'
import { humanizeTimestamp } from '../utils/date'
import ActionRequestStatusChip from './ActionRequestStatusChip'
import { ActionRequestAction } from '../enums/ActionRequestAction'
import { RequesterType } from '../enums/RequesterType'
import { RequestableType } from '../enums/RequestableType'
import ActionRequestRejectButton from './ActionRequestRejectButton'
import { Stack } from '@mui/system'
import ActionRequestAcceptButton from './ActionRequestAcceptButton'
import ActionRequestDeviceLocationChangedButton from './ActionRequestDeviceLocationChangedButton'
import { formatDistanceToNow } from 'date-fns'
import ActionRequestExtendProjectButton from './ActionRequestExtendProjectButton'
import { isProjectActionRequest } from '../utils/actionRequest'
import ActionRequestPriorityChip from './ActionRequestPriorityChip'
import { usePolicy } from '../hooks'
import SelectCountryCodeButton from './SelectCountryCodeButton'
import { useEffect, useState } from 'react'
import { createErrorNotification } from '../redux/slices/notifications'
import { getProject } from '../services/projectService'
import { useDispatch } from 'react-redux'

interface ActionRequestDataGridActionsProps {
  actionRequest: ActionRequest
  afterAccept?: (actionRequest: ActionRequest) => void
  afterReject?: (actionRequest: ActionRequest) => void
}

function ActionRequestDataGridActions({
  actionRequest,
  afterAccept,
  afterReject
}: ActionRequestDataGridActionsProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('actionRequest')

  const { canUpdateActionRequestState } = usePolicy()

  if (!canUpdateActionRequestState(actionRequest)) {
    return null
  }

  switch (actionRequest.action) {
    case ActionRequestAction.SELECT_COUNTRY_CODE:
      return <CountryCodeAction actionRequest={actionRequest} afterAccept={afterAccept} />
    case ActionRequestAction.ENABLE_ALERT_NOTIFICATIONS:
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <ActionRequestAcceptButton
            label={t('enable')}
            actionRequest={actionRequest}
            afterSubmit={afterAccept}
          />
          <ActionRequestRejectButton actionRequest={actionRequest} afterSubmit={afterReject} />
        </Stack>
      )
    case ActionRequestAction.JOIN_COMPANY:
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <ActionRequestAcceptButton actionRequest={actionRequest} afterSubmit={afterAccept} />
          <ActionRequestRejectButton actionRequest={actionRequest} afterSubmit={afterReject} />
        </Stack>
      )
    case null:
      switch (actionRequest.requestableType) {
        case RequestableType.COMPANY:
          switch (actionRequest.requesterType) {
            case RequesterType.USER:
              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <ActionRequestAcceptButton
                    actionRequest={actionRequest}
                    afterSubmit={afterAccept}
                  />
                  <ActionRequestRejectButton
                    actionRequest={actionRequest}
                    afterSubmit={afterReject}
                  />
                </Stack>
              )
            default:
              return null
          }
        case RequestableType.PROJECT:
          switch (actionRequest.requesterType) {
            case RequesterType.SYSTEM:
              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <ActionRequestAcceptButton
                    label={t('endProject')}
                    actionRequest={actionRequest}
                    afterSubmit={afterAccept}
                  />
                  <ActionRequestExtendProjectButton
                    actionRequest={actionRequest}
                    color="warning"
                    afterSubmit={afterReject}
                  />
                </Stack>
              )
            case RequesterType.USER:
              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <ActionRequestAcceptButton
                    actionRequest={actionRequest}
                    afterSubmit={afterAccept}
                  />
                  <ActionRequestRejectButton
                    actionRequest={actionRequest}
                    afterSubmit={afterReject}
                  />
                </Stack>
              )
            case RequesterType.DEVICE:
              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <ActionRequestDeviceLocationChangedButton
                    actionRequest={actionRequest}
                    afterAccept={afterAccept}
                    afterReject={afterReject}
                  />
                  <ActionRequestRejectButton
                    actionRequest={actionRequest}
                    afterSubmit={afterReject}
                  />
                </Stack>
              )
            default:
              return null
          }
        default:
          return null
      }
    default:
      return null
  }
}

interface CountryCodeActionProps {
  actionRequest: ActionRequest
  afterAccept?: (actionRequest: ActionRequest) => void
}

function CountryCodeAction({ actionRequest, afterAccept }: CountryCodeActionProps) {
  const [project, setProject] = useState<Project | undefined>()

  const { t } = useTranslation('actionRequest')
  const dispatch = useDispatch()

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setProject(await getProject(parseInt(actionRequest.requestableId)))
      } catch (_error) {
        dispatch(createErrorNotification(t('fetchingProjectFailed')))
      }
    }
    fetchProject()
  }, [actionRequest.requestableId, dispatch, t])

  function afterSubmit() {
    afterAccept?.(actionRequest)
  }

  return project === undefined ? (
    <></>
  ) : (
    <SelectCountryCodeButton
      actionRequest={actionRequest}
      project={project}
      handleAfterSubmit={afterSubmit}
    />
  )
}

interface ActionRequestDataGridProps extends Omit<DataGridProps, 'rows' | 'columns'> {
  actionRequests: ActionRequest[]
  project?: Project
  company?: Company
  user?: User
  device?: Device
  ruuviTag?: RuuviTag
  afterAccept?: (actionRequest: ActionRequest) => void
  afterReject?: (actionRequest: ActionRequest) => void
}

export default function ActionRequestDataGrid({
  actionRequests,
  project,
  company,
  user,
  device,
  ruuviTag,
  afterAccept,
  afterReject,
  ...props
}: ActionRequestDataGridProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('actionRequest')

  /**
   * The columns.
   */
  const columns: GridColDef<ActionRequest>[] = [
    {
      field: 'priority',
      headerName: t('priority'),
      type: 'string',
      width: 150,
      renderCell: ({ row }) => <ActionRequestPriorityChip priority={row.priority} size="small" />
    },
    {
      field: 'createdAt',
      headerName: t('timestamp'),
      type: 'date',
      width: 200,
      valueGetter: (_, row) => new Date(row.createdAt),
      valueFormatter: (_, row) => humanizeTimestamp(row.createdAt)
    },
    {
      field: 'action',
      headerName: t('action'),
      type: 'string',
      flex: 1,
      valueFormatter: (_, row) => translateAction(row)
    },
    {
      field: 'status',
      headerName: t('status'),
      type: 'string',
      width: 150,
      renderCell: ({ row }) => <ActionRequestStatusChip status={row.status} size="small" />
    },
    {
      field: 'actions',
      type: 'actions',
      width: 300,
      headerName: t('actions'),
      renderCell: ({ row }) => (
        <ActionRequestDataGridActions
          actionRequest={row}
          afterAccept={afterAccept}
          afterReject={afterReject}
        />
      )
    }
  ]

  /**
   * Translate an action of a action request.
   */
  function translateAction(actionRequest: ActionRequest) {
    switch (actionRequest.action) {
      case ActionRequestAction.ENABLE_ALERT_NOTIFICATIONS:
        return translateEnableAlertNotificationsAction(actionRequest)
      case ActionRequestAction.SELECT_COUNTRY_CODE:
        return formatSelectCountryCodeAction(actionRequest)
      case ActionRequestAction.SELECT_COORDINATES:
        return formatSelectCoordinatesAction(actionRequest)
      case ActionRequestAction.SELECT_EIC_AREA_CODE:
        return formatSelectEicAreaCodeAction(actionRequest)
      case ActionRequestAction.JOIN_COMPANY:
        return translateJoinCompanyAction(actionRequest)
      default:
        return translateUnknownAction(actionRequest)
    }
  }

  /**
   * Translate "Enable alert notification" action.
   */
  function translateEnableAlertNotificationsAction(actionRequest: ActionRequest) {
    switch (actionRequest.requestableType) {
      case RequestableType.PROJECT:
        return translateEnableAlertNotificationsActionForProject(actionRequest)
      case RequestableType.DEVICE:
        return translateEnableAlertNotificationsActionForDevice(actionRequest)
      case RequestableType.RUUVI_TAG:
        return translateEnableAlertNotificationsActionForRuuviTag(actionRequest)
      default:
        return translateEnableAlertNotificationsActionForRequestable(actionRequest)
    }
  }

  /**
   * Translate "Enable alert notification" action for a project.
   */
  function translateEnableAlertNotificationsActionForProject(actionRequest: ActionRequest) {
    return project
      ? t('enableAlertNotifications')
      : t('enableAlertNotificationsForProject', {
          project: humanizeRequestableProject(actionRequest)
        })
  }

  /**
   * Translate "Enable alert notification" action for a device.
   */
  function translateEnableAlertNotificationsActionForDevice(actionRequest: ActionRequest) {
    return device
      ? t('enableAlertNotifications')
      : t('enableAlertNotificationsForAsset', {
          asset: humanizeRequestableDevice(actionRequest)
        })
  }

  /**
   * Translate "Enable alert notification" action for a RuuviTag.
   */
  function translateEnableAlertNotificationsActionForRuuviTag(actionRequest: ActionRequest) {
    return ruuviTag
      ? t('enableAlertNotifications')
      : t('enableAlertNotificationsForAsset', {
          asset: humanizeRequestableRuuviTag(actionRequest)
        })
  }

  /**
   * Translate "Enable alert notification" action for a requestable.
   */
  function translateEnableAlertNotificationsActionForRequestable(actionRequest: ActionRequest) {
    return t('enableAlertNotificationsForRequestable', {
      requestable: humanizeRequestable(actionRequest)
    })
  }

  /**
   * Translate "Select country code" action.
   */
  function formatSelectCountryCodeAction(actionRequest: ActionRequest) {
    return project
      ? t('selectCountryCode')
      : t('selectCountryCodeForProject', {
          project: humanizeRequestableProject(actionRequest)
        })
  }

  /**
   * Translate "Select coordinates" action.
   */
  function formatSelectCoordinatesAction(actionRequest: ActionRequest) {
    return project
      ? t('selectCoordinates')
      : t('selectCoordinatesForProject', {
          project: humanizeRequestableProject(actionRequest)
        })
  }

  /**
   * Translate a "Select EIC area code" action.
   */
  function formatSelectEicAreaCodeAction(actionRequest: ActionRequest) {
    return project
      ? t('selectEicAreaCode')
      : t('selectEicAreaCodeForProject', {
          project: humanizeRequestableProject(actionRequest)
        })
  }

  function translateJoinCompanyAction(actionRequest: ActionRequest) {
    return t('requestsAccessToCompany', {
      user: humanizeRequesterUser(actionRequest),
      company: humanizeRequestableCompany(actionRequest)
    })
  }

  /**
   * Translate an unknown action.
   */
  function translateUnknownAction(actionRequest: ActionRequest) {
    switch (actionRequest.requestableType) {
      case RequestableType.COMPANY:
        return translateUnknownActionToCompany(actionRequest)
      case RequestableType.PROJECT:
        return translateUnknownActionToProject(actionRequest)
      case RequestableType.USER:
        return translateUnknownActionToUser(actionRequest)
      case RequestableType.DEVICE:
        return translateUnknownActionToDevice(actionRequest)
      case RequestableType.RUUVI_TAG:
        return translateUnknownActionToRuuviTag(actionRequest)
      default:
        return translateUnknownActionToRequestable(actionRequest)
    }
  }

  /**
   * Translate an unknown action for a company.
   */
  function translateUnknownActionToCompany(actionRequest: ActionRequest) {
    switch (actionRequest.requesterType) {
      case RequesterType.SYSTEM:
        return translateUnknownActionFromSystemToCompany(actionRequest)
      case RequesterType.USER:
        return translateUnknownActionFromUserToCompany(actionRequest)
      case RequesterType.DEVICE:
        return translateUnknownActionFromDeviceToCompany(actionRequest)
      default:
        return translateUnknownActionFromRequesterToCompany(actionRequest)
    }
  }

  /**
   * Translate an unknown action from system to a company.
   */
  function translateUnknownActionFromSystemToCompany(actionRequest: ActionRequest) {
    return company
      ? t('unknownActionRequestFromSystem')
      : t('unknownActionRequestFromSystemToCompany', {
          company: humanizeRequestableCompany(actionRequest)
        })
  }

  /**
   * Translate an unknown action from user to a company.
   */
  function translateUnknownActionFromUserToCompany(actionRequest: ActionRequest) {
    return user
      ? t('requestsRoleToCompany', {
          role: actionRequest.role ?? t('unknownRole'),
          company: humanizeRequestableCompany(actionRequest)
        })
      : company
        ? t('common:roleRequested', {
            user: humanizeRequesterUser(actionRequest),
            role: actionRequest.role ?? t('unknownRole'),
            company: humanizeRequestableCompany(actionRequest)
          })
        : t('userRequestsRoleToCompany', {
            user: humanizeRequesterUser(actionRequest),
            role: actionRequest.role ?? t('unknownRole'),
            company: humanizeRequestableCompany(actionRequest)
          })
  }

  /**
   * Translate an unknown action from device to a company.
   */
  function translateUnknownActionFromDeviceToCompany(actionRequest: ActionRequest) {
    return company
      ? t('unknownActionRequestFromDevice', {
          device: humanizeRequesterDevice(actionRequest)
        })
      : device
        ? t('unknownActionRequestToCompany', {
            company: humanizeRequestableCompany(actionRequest)
          })
        : t('unknownActionRequestFromDeviceToCompany', {
            device: humanizeRequesterDevice(actionRequest),
            company: humanizeRequestableCompany(actionRequest)
          })
  }

  /**
   * Translate an unknown action from requester to a company.
   */
  function translateUnknownActionFromRequesterToCompany(actionRequest: ActionRequest) {
    return company
      ? t('unknownActionRequestFromRequester', {
          requester: humanizeRequester(actionRequest)
        })
      : t('unknownActionRequestFromRequesterToCompany', {
          requester: humanizeRequester(actionRequest),
          company: humanizeRequestableCompany(actionRequest)
        })
  }

  /**
   * Translate an unknown action to a project.
   */
  function translateUnknownActionToProject(actionRequest: ActionRequest) {
    switch (actionRequest.requesterType) {
      case RequesterType.SYSTEM:
        return translateUnknownActionFromSystemToProject(actionRequest)
      case RequesterType.USER:
        return translateUnknownActionFromUserToProject(actionRequest)
      case RequesterType.DEVICE:
        return translateUnknownActionFromDeviceToProject(actionRequest)
      default:
        return translateUnknownActionFromRequesterToProject(actionRequest)
    }
  }

  function getTimeAgo(date: string | Date) {
    return formatDistanceToNow(new Date(date))
  }

  /**
   * Translate an unknown action from system to a project.
   */
  function translateUnknownActionFromSystemToProject(actionRequest: ActionRequest) {
    if (!isProjectActionRequest(actionRequest)) {
      return ''
    }

    if (project && project.endDate) {
      return t('thisProjectHasEndedTimeAgo', {
        timeAgo: getTimeAgo(project.endDate)
      })
    }

    if (project) {
      return t('thisProjectHasEnded')
    }

    const requestable = actionRequest?.requestable
    const requestableEndDate = requestable?.endDate

    if (requestable && requestableEndDate) {
      return t('projectHasEndedTimeAgo', {
        project: humanizeRequestableProject(actionRequest),
        timeAgo: getTimeAgo(requestableEndDate)
      })
    }

    return t('projectHasEnded', {
      project: humanizeRequestableProject(actionRequest)
    })
  }

  /**
   * Translate an unknown action from user to a project.
   */
  function translateUnknownActionFromUserToProject(actionRequest: ActionRequest) {
    return user
      ? t('requestsProjectMembershipToProject', {
          project: humanizeRequestableProject(actionRequest)
        })
      : project
        ? t('userRequestsProjectMembership', {
            user: humanizeRequesterUser(actionRequest)
          })
        : t('userRequestsProjectMembershipToProject', {
            user: humanizeRequesterUser(actionRequest),
            project: humanizeRequestableProject(actionRequest)
          })
  }

  /**
   * Translate an unknown action by device for a project.
   */
  function translateUnknownActionFromDeviceToProject(actionRequest: ActionRequest) {
    return device
      ? t('deviceLocationHasChangedOnProject', {
          project: humanizeRequestableProject(actionRequest)
        })
      : project
        ? t('deviceLocationForDeviceHasChanged', {
            device: humanizeRequesterDevice(actionRequest)
          })
        : t('deviceLocationForDeviceHasChangedOnProject', {
            device: humanizeRequesterDevice(actionRequest),
            project: humanizeRequestableProject(actionRequest)
          })
  }

  /**
   * Translate an unknown action from requester to a project.
   */
  function translateUnknownActionFromRequesterToProject(actionRequest: ActionRequest) {
    return project
      ? t('unknownActionRequestFromRequester', {
          requester: humanizeRequester(actionRequest)
        })
      : t('unknownActionRequestFromRequesterToProject', {
          requester: humanizeRequester(actionRequest),
          project: humanizeRequestableProject(actionRequest)
        })
  }

  /**
   * Translate an unknown action to a user.
   */
  function translateUnknownActionToUser(actionRequest: ActionRequest) {
    switch (actionRequest.requesterType) {
      case RequesterType.SYSTEM:
        return translateUnknownActionFromSystemToUser(actionRequest)
      case RequesterType.USER:
        return translateUnknownActionFromUserToUser(actionRequest)
      case RequesterType.DEVICE:
        return translateUnknownActionFromDeviceToUser(actionRequest)
      default:
        return translateUnknownActionFromRequesterToUser(actionRequest)
    }
  }

  /**
   * Translate an unknown action from system to a user.
   */
  function translateUnknownActionFromSystemToUser(actionRequest: ActionRequest) {
    return user
      ? t('unknownActionRequestFromSystem')
      : t('unknownActionRequestFromSystemToUser', {
          user: humanizeRequestableUser(actionRequest)
        })
  }

  /**
   * Translate an unknown action from user to a user.
   */
  function translateUnknownActionFromUserToUser(actionRequest: ActionRequest) {
    return user
      ? t('unknownActionRequestToUser', {
          user: humanizeRequesterUser(actionRequest)
        })
      : t('unknownActionRequestFromUserToUser', {
          fromUser: humanizeRequesterUser(actionRequest),
          user: humanizeRequestableUser(actionRequest)
        })
  }

  /**
   * Translate an unknown action from device to a user.
   */
  function translateUnknownActionFromDeviceToUser(actionRequest: ActionRequest) {
    return user
      ? t('unknownActionRequestFromDevice', {
          device: humanizeRequesterDevice(actionRequest)
        })
      : device
        ? t('unknownActionRequestToUser', {
            user: humanizeRequestableUser(actionRequest)
          })
        : t('unknownActionRequestFromDeviceToUser', {
            device: humanizeRequesterDevice(actionRequest),
            user: humanizeRequestableUser(actionRequest)
          })
  }

  /**
   * Translate an unknown action from requester to a user.
   */
  function translateUnknownActionFromRequesterToUser(actionRequest: ActionRequest) {
    return user
      ? t('unknownActionRequestFromRequester', {
          requester: humanizeRequester(actionRequest)
        })
      : t('unknownActionRequestFromRequesterToUser', {
          requester: humanizeRequester(actionRequest),
          user: humanizeRequestableUser(actionRequest)
        })
  }

  /**
   * Translate an unknown action for a device.
   */
  function translateUnknownActionToDevice(actionRequest: ActionRequest) {
    switch (actionRequest.requesterType) {
      case RequesterType.SYSTEM:
        return translateUnknownActionFromSystemToDevice(actionRequest)
      case RequesterType.USER:
        return translateUnknownActionFromUserToDevice(actionRequest)
      case RequesterType.DEVICE:
        return translateUnknownActionFromDeviceToDevice(actionRequest)
      default:
        return translateUnknownActionFromRequesterToDevice(actionRequest)
    }
  }

  /**
   * Translate an unknown action from system to a device.
   */
  function translateUnknownActionFromSystemToDevice(actionRequest: ActionRequest) {
    return device
      ? t('unknownActionRequestFromSystem')
      : t('unknownActionRequestFromSystemToAsset', {
          asset: humanizeRequestableDevice(actionRequest)
        })
  }

  /**
   * Translate an unknown action from user to a device.
   */
  function translateUnknownActionFromUserToDevice(actionRequest: ActionRequest) {
    return device
      ? t('unknownActionRequestFromUser', {
          user: humanizeRequesterUser(actionRequest)
        })
      : user
        ? t('unknownActionRequestToAsset', {
            asset: humanizeRequestableDevice(actionRequest)
          })
        : t('unknownActionRequestFromUserToAsset', {
            user: humanizeRequesterUser(actionRequest),
            asset: humanizeRequestableDevice(actionRequest)
          })
  }

  /**
   * Translate an unknown action from device to a device.
   */
  function translateUnknownActionFromDeviceToDevice(actionRequest: ActionRequest) {
    return device
      ? t('unknownActionRequestFromDevice', {
          device: humanizeRequesterDevice(actionRequest)
        })
      : t('unknownActionRequestFromDeviceToAsset', {
          device: humanizeRequesterDevice(actionRequest),
          asset: humanizeRequestableDevice(actionRequest)
        })
  }

  /**
   * Translate an unknown action from requester to a device.
   */
  function translateUnknownActionFromRequesterToDevice(actionRequest: ActionRequest) {
    return device
      ? t('unknownActionRequestFromRequester', {
          requester: humanizeRequester(actionRequest)
        })
      : t('unknownActionRequestFromRequesterToDevice', {
          requester: humanizeRequester(actionRequest),
          device: humanizeRequestableDevice(actionRequest)
        })
  }

  /**
   * Translate an unknown action to a RuuviTag.
   */
  function translateUnknownActionToRuuviTag(actionRequest: ActionRequest) {
    switch (actionRequest.requesterType) {
      case RequesterType.SYSTEM:
        return translateUnknownActionToFromSystemToRuuviTag(actionRequest)
      case RequesterType.USER:
        return translateUnknownActionToFromUserToRuuviTag(actionRequest)
      case RequesterType.DEVICE:
        return translateUnknownActionToFromDeviceToRuuviTag(actionRequest)
      default:
        return translateUnknownActionToFromRequesterToRuuviTag(actionRequest)
    }
  }

  /**
   * Translate an unknown action from system to a RuuviTag.
   */
  function translateUnknownActionToFromSystemToRuuviTag(actionRequest: ActionRequest) {
    return ruuviTag
      ? t('unknownActionRequestFromSystem')
      : t('unknownActionRequestFromSystemToAsset', {
          asset: humanizeRequestableRuuviTag(actionRequest)
        })
  }

  /**
   * Translate an unknown action from a user to a RuuviTag.
   */
  function translateUnknownActionToFromUserToRuuviTag(actionRequest: ActionRequest) {
    return ruuviTag
      ? t('unknownActionRequestFromUser', {
          user: humanizeRequestableUser(actionRequest)
        })
      : user
        ? t('unknownActionRequestToAsset', {
            asset: humanizeRequestableRuuviTag(actionRequest)
          })
        : t('unknownActionRequestFromUserToAsset', {
            user: humanizeRequestableUser(actionRequest),
            asset: humanizeRequestableRuuviTag(actionRequest)
          })
  }

  /**
   * Translate an unknown action from a device to a RuuviTag.
   */
  function translateUnknownActionToFromDeviceToRuuviTag(actionRequest: ActionRequest) {
    return ruuviTag
      ? t('unknownActionRequestFromDevice', {
          device: humanizeRequesterDevice(actionRequest)
        })
      : device
        ? t('unknownActionRequestToAsset', {
            asset: humanizeRequestableRuuviTag(actionRequest)
          })
        : t('unknownActionRequestFromDeviceToAsset', {
            device: humanizeRequesterDevice(actionRequest),
            asset: humanizeRequestableRuuviTag(actionRequest)
          })
  }

  /**
   * Translate an unknown action from a device to a RuuviTag.
   */
  function translateUnknownActionToFromRequesterToRuuviTag(actionRequest: ActionRequest) {
    return ruuviTag
      ? t('unknownActionRequestFromRequester', {
          requester: humanizeRequester(actionRequest)
        })
      : t('unknownActionRequestFromRequesterToRuuviTag', {
          requester: humanizeRequester(actionRequest),
          asset: humanizeRequestableRuuviTag(actionRequest)
        })
  }

  /**
   * Translate an unknown action to requestable.
   */
  function translateUnknownActionToRequestable(actionRequest: ActionRequest) {
    switch (actionRequest.requesterType) {
      case RequesterType.SYSTEM:
        return translateUnknownActionFromSystemToRequestable(actionRequest)
      case RequesterType.USER:
        return translateUnknownActionFromUserToRequestable(actionRequest)
      case RequesterType.DEVICE:
        return translateUnknownActionFromDeviceToRequestable(actionRequest)
      default:
        return translateUnknownActionFromRequesterToRequestable(actionRequest)
    }
  }

  /**
   * Translate an unknown action request from system to requestable.
   */
  function translateUnknownActionFromSystemToRequestable(actionRequest: ActionRequest) {
    return t('unknownActionRequestFromSystemToRequestable', {
      requestable: humanizeRequestable(actionRequest)
    })
  }

  /**
   * Translate an unknown action request from user to requestable.
   */
  function translateUnknownActionFromUserToRequestable(actionRequest: ActionRequest) {
    return user
      ? t('unknownActionRequestToRequestable', {
          requestable: humanizeRequestable(actionRequest)
        })
      : t('unknownActionRequestFromUserToRequestable', {
          user: humanizeRequesterUser(actionRequest),
          requestable: humanizeRequestable(actionRequest)
        })
  }

  /**
   * Translate an unknown action request from device to requestable.
   */
  function translateUnknownActionFromDeviceToRequestable(actionRequest: ActionRequest) {
    return device
      ? t('unknownActionRequestToRequestable', {
          requestable: humanizeRequestable(actionRequest)
        })
      : t('unknownActionRequestFromDeviceToRequestable', {
          device: humanizeRequesterDevice(actionRequest),
          requestable: humanizeRequestable(actionRequest)
        })
  }

  /**
   * Translate an unknown action from requester to requestable.
   */
  function translateUnknownActionFromRequesterToRequestable(actionRequest: ActionRequest) {
    return t('unknownActionRequestFromRequesterToRequestable', {
      requester: humanizeRequester(actionRequest),
      requestable: humanizeRequestable(actionRequest)
    })
  }

  /**
   * Get a human-readable value of the requester.
   */
  function humanizeRequester(actionRequest: ActionRequest): string {
    return `${actionRequest.requestableType} #${actionRequest.requesterId}`
  }

  /**
   * Get a human-readable value of the requester user.
   */
  function humanizeRequesterUser(actionRequest: ActionRequest): string {
    const user = actionRequest.requester as User

    return user ? `${user.firstName} ${user.lastName}` : `#${actionRequest.requesterId}`
  }

  /**
   * Get a human-readable value of the requester device.
   */
  function humanizeRequesterDevice(actionRequest: ActionRequest): string {
    const device = actionRequest.requester as Device

    return device?.asset?.name ?? device?.name ?? `#${actionRequest.requesterId}`
  }

  /**
   * GEt human-readable value of the requestable.
   */
  function humanizeRequestable(actionRequest: ActionRequest): string {
    return `${actionRequest.requestableType} #${actionRequest.requestableId}`
  }

  /**
   * Get a human-readable value of the requestable company.
   */
  function humanizeRequestableCompany(actionRequest: ActionRequest): string {
    const company = actionRequest.requestable as Company
    return company?.name ?? `#${actionRequest.requestableId}`
  }

  /**
   * Get a human-readable value of the requestable project.
   */
  function humanizeRequestableProject(actionRequest: ActionRequest): string {
    return isProjectActionRequest(actionRequest) && actionRequest.requestable?.name
      ? actionRequest.requestable.name
      : `#${actionRequest.requestableId}`
  }

  /**
   * Get a human-readable value of the requestable user.
   */
  function humanizeRequestableUser(actionRequest: ActionRequest): string {
    const user = actionRequest.requestable as User

    return user ? `${user?.firstName} ${user?.lastName}` : `#${actionRequest.requestableId}`
  }

  /**
   * Get a human-readable value of the requestable device.
   */
  function humanizeRequestableDevice(actionRequest: ActionRequest): string {
    const device = actionRequest.requestable as Device

    return device?.asset?.name ?? device?.name ?? `#${actionRequest.requestableId}`
  }

  /**
   * Get a human-readable value of the requestable RuuviTag.
   */
  function humanizeRequestableRuuviTag(actionRequest: ActionRequest): string {
    const ruuviTag = actionRequest.requestable as RuuviTag

    return ruuviTag?.asset?.name ?? ruuviTag?.id ?? `#${actionRequest.requestableId}`
  }

  return <DataGrid columns={columns} rows={actionRequests} {...props} />
}
